import { useEffect, useState } from "react";
import type { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useFormat } from "../../context/FormatContext";
import DataTable from "../components/DataTable";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import AdminDataModal from "../components/AdminDataModal";

export default function AdminBookDemo() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

  const { showModal } = useMessageModal();
  const { formatDateToDMY } = useFormat();

  const [data, setData] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"edit" | "view">("view");
  const [activeRow, setActiveRow] = useState<any>(null);

  const fetchDemoBookings = () => {
    fetch(`${BASE_URL}/admin/book-demo`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((res) => setData(res.data || []))
      .catch((error) => {
        console.error("Fetch Demo Bookings Error:", error);
      });
  };

  useEffect(() => {
    fetchDemoBookings();
  }, []);

  const formatDateForInput = (dateString: string) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const handleUpdate = async (formData: any) => {
    try {
      const res = await fetch(`${BASE_URL}/admin/book-demo/${activeRow.id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          demo_type: formData.demo_type,
          booking_date: formData.booking_date,
          booking_time: formData.booking_time,
          status: formData.status,
          cancellation_reason:
            formData.status === "cancelled"
              ? formData.cancellation_reason
              : null,
        }),
      });

      const result = await res.json();

      if (result.success) {
        fetchDemoBookings();
        setModalOpen(false);

        showModal({
          type: "success",
          title: "Updated",
          message: "Demo booking updated successfully.",
          confirmText: "OK",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Failed to update demo booking.",
        });
      }
    } catch (error) {
      console.error("Update Demo Booking Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Failed to update demo booking.",
      });
    }
  };

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Demo Booking",
      message: "Are you sure you want to delete this demo booking?",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(`${BASE_URL}/admin/book-demo/${row.id}`, {
            method: "DELETE",
            credentials: "include",
          });

          const result = await res.json();

          if (result.success) {
            fetchDemoBookings();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Demo booking deleted successfully.",
            });
          } else {
            showModal({
              type: "error",
              title: "Error",
              message: result.message || "Failed to delete demo booking.",
            });
          }
        } catch {
          showModal({
            type: "error",
            title: "Error",
            message: "Failed to delete demo booking.",
          });
        }
      },
    });
  };

  const cancellationReasonOptions = [
    { label: "Client No Show", value: "Client No Show" },
    {
      label: "Client Requested Cancellation",
      value: "Client Requested Cancellation",
    },
    { label: "Reschedule Requested", value: "Reschedule Requested" },
    { label: "Unable to Contact Client", value: "Unable to Contact Client" },
    { label: "Wrong Booking Details", value: "Wrong Booking Details" },
    { label: "Duplicate Booking", value: "Duplicate Booking" },
    { label: "Not Interested", value: "Not Interested" },
    { label: "Other", value: "Other" },
  ];

  const demoBookingFields: FormField[] = [
    { key: "id", label: "ID", colSpan: 2 },

    {
      key: "subscriber_image",
      label: "Subscriber Image",
      type: "image",
      imageType: "single",
      imageFolder: "subscribers",
      disabled: true,
      colSpan: 2,
    },

    {
      key: "subscriber_name",
      label: "Subscriber Name",
      disabled: true,
      colSpan: 1,
    },

    {
      key: "subscriber_email",
      label: "Subscriber Email",
      disabled: true,
      colSpan: 1,
    },

    {
      key: "software_title",
      label: "Software",
      disabled: true,
      colSpan: 2,
    },

    {
      key: "demo_type",
      label: "Demo Type",
      type: "select",
      options: [
        { label: "Online", value: "online" },
        { label: "On Site", value: "onsite" },
      ],
      colSpan: 1,
    },

    {
      key: "booking_date",
      label: "Booking Date",
      type: "date",
      colSpan: 1,
    },

    {
      key: "booking_time",
      label: "Booking Time",
      type: "text",
      colSpan: 1,
    },

    {
      key: "status",
      label: "Status",
      type: "select",
      options: [
        { label: "Pending", value: "pending" },
        { label: "Confirmed", value: "confirmed" },
        { label: "Completed", value: "completed" },
        { label: "Cancelled", value: "cancelled" },
      ],
      colSpan: 1,
    },

    {
      key: "cancellation_reason",
      label: "Reason",
      type: "select",
      options: cancellationReasonOptions,
      colSpan: 1,
      showWhen: {
        key: "status",
        value: "cancelled",
      },
    },
  ];

  const columns: Column[] = [
    { key: "id", label: "ID" },

    {
      key: "subscriber_image",
      label: "Image",
      imageFolder: "subscribers",
    },

    { key: "subscriber_name", label: "Name" },

    { key: "subscriber_email", label: "Email" },

    { key: "software_title", label: "Software" },

    {
      key: "demo_type",
      label: "Demo Type",
      render: (row: any) => (row.demo_type === "onsite" ? "On Site" : "Online"),
    },

    {
      key: "booking_date",
      label: "Date",
      render: (row) =>
        row.booking_date ? formatDateForInput(row.booking_date) : "-",
    },

    {
      key: "booking_time",
      label: "Time",
      render: (row: any) => row.booking_time || "-",
    },

    {
      key: "status",
      label: "Status",
      render: (row: any) => (
        <span
          className={
            row.status === "completed" || row.status === "confirmed"
              ? "badge-success"
              : row.status === "cancelled"
                ? "badge-danger"
                : "badge-warning"
          }
        >
          {row.status
            ? row.status.charAt(0).toUpperCase() + row.status.slice(1)
            : "-"}
        </span>
      ),
    },

    {
      key: "cancellation_reason",
      label: "Reason",
      render: (row: any) =>
        row.status === "cancelled" ? row.cancellation_reason || "-" : "-",
    },

    {
      key: "createdAt",
      label: "Created",
      render: (row: any) =>
        row.createdAt ? formatDateToDMY(row.createdAt) : "-",
    },
  ];

  return (
    <div>
      <AdminListToolkit
        title="Demo Bookings"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onView={(row) => {
          setModalMode("view");
          const formattedRow = {
            ...row,
            booking_date: formatDateForInput(row.booking_date),
            cancellation_reason: row.cancellation_reason || "",
          };
          setActiveRow(formattedRow);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          const formattedRow = {
            ...row,
            booking_date: formatDateForInput(row.booking_date),
            cancellation_reason: row.cancellation_reason || "",
          };
          setActiveRow(formattedRow);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={true}
        exportFileName="book-demo"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Demo Bookings"
        fields={demoBookingFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleUpdate}
      />
    </div>
  );
}
